import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, SeriesType } from '@chart';
import { ChartSearchParams } from '@terminal/utils/chart-search-utils/chart-search-utils';

const VALID_SERIES_TYPES: SeriesType[] = ['line', 'bars', 'candlestick'];

const EXCLUDED_HORIZONS: RangeHorizon[] = [
  RangeHorizon.TwoWeeks,
  RangeHorizon.TwoMonths,
  RangeHorizon.Max,
];

const KEYS_TO_STRIP = [
  ChartSearchParams.Domain,
  ChartSearchParams.Primary,
  ChartSearchParams.Resample,
  ChartSearchParams.Hidden,
] as const;

export const SERIES_TYPE_PARAM = 'seriesType';

export const isChartPrimaryForEntity = (
  primary: string | null,
  entity: Entity
) => {
  if (!primary) {
    return true;
  }

  return (
    primary === entity.default_snake || primary.startsWith(`${entity.tag}.`)
  );
};

export const parseChartHorizon = (
  value: string | null
): RangeHorizon | undefined => {
  if (!value) {
    return undefined;
  }

  const horizon = Object.values(RangeHorizon).find(item => item === value);

  if (!horizon || EXCLUDED_HORIZONS.includes(horizon)) {
    return undefined;
  }

  return horizon;
};

export const parseChartSeriesType = (
  value: string | null
): SeriesType | undefined => {
  if (!value) {
    return undefined;
  }

  return VALID_SERIES_TYPES.find(item => item === value);
};

export interface ChartParams {
  horizon: RangeHorizon;
  seriesType?: SeriesType;
  keepParams: boolean;
}

export const resolveChartParams = ({
  searchParams,
  entity,
  fallbackHorizon,
}: {
  searchParams: URLSearchParams;
  entity: Entity;
  fallbackHorizon: RangeHorizon;
}): ChartParams => {
  const keepParams = isChartPrimaryForEntity(
    searchParams.get(ChartSearchParams.Primary),
    entity
  );

  if (!keepParams) {
    return {
      horizon: fallbackHorizon,
      keepParams: false,
    };
  }

  return {
    horizon:
      parseChartHorizon(searchParams.get(ChartSearchParams.Horizon)) ??
      fallbackHorizon,
    seriesType: parseChartSeriesType(searchParams.get(SERIES_TYPE_PARAM)),
    keepParams: true,
  };
};

export const getChartAssetTag = (searchParams: URLSearchParams) => {
  const asset = searchParams.get(ChartSearchParams.Asset);

  return asset?.split(';')[0] || null;
};

export const stripChartParams = (
  searchParams: URLSearchParams,
  keepParams: boolean
) => {
  const next = new URLSearchParams(searchParams);

  KEYS_TO_STRIP.forEach(key => {
    next.delete(key);
  });

  if (!keepParams) {
    next.delete(ChartSearchParams.Horizon);
    next.delete(SERIES_TYPE_PARAM);
  }

  return next;
};

export const withChartParams = (
  searchParams: URLSearchParams,
  {
    horizon,
    seriesType,
  }: {
    horizon: RangeHorizon | null;
    seriesType: SeriesType;
  }
) => {
  const next = new URLSearchParams(searchParams);

  if (horizon) {
    next.set(ChartSearchParams.Horizon, horizon);
  } else {
    next.delete(ChartSearchParams.Horizon);
  }

  next.set(SERIES_TYPE_PARAM, seriesType);

  return next;
};
